import { useEffect } from 'react';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { setApiTokenProvider, setMsalContext } from '../services/api/baseApi';
import { acquireBackendToken, isBackendRequestUrl, withAuthHeader } from '../utils/authApi';

const PROACTIVE_REFRESH_MS = 50 * 60 * 1000;

function ApiAuthProvider({ children }) {
  const { instance, accounts } = useMsal();

  useEffect(() => {
    const account = accounts[0] || null;
    setMsalContext(instance, account);

    setApiTokenProvider(async () => {
      if (!account) {
        return null;
      }

      const tokenResult = await acquireBackendToken(instance, account);
      return tokenResult?.accessToken || null;
    });

    return () => {
      setMsalContext(null, null);
    };
  }, [instance, accounts]);

  useEffect(() => {
    if (!accounts.length) {
      return undefined;
    }

    const account = accounts[0];
    let cancelled = false;

    const refreshInBackground = async () => {
      if (cancelled) {
        return;
      }

      try {
        await acquireBackendToken(instance, account, { forceRefresh: true });
      } catch (error) {
        console.warn('Background token refresh failed:', error.message);
      }
    };

    const intervalId = window.setInterval(refreshInBackground, PROACTIVE_REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [instance, accounts]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        if (
          status !== 401 ||
          !originalRequest ||
          originalRequest._authRetry ||
          !isBackendRequestUrl(originalRequest.url || '', originalRequest.baseURL || '')
        ) {
          return Promise.reject(error);
        }

        if (!accounts.length) {
          return Promise.reject(error);
        }

        originalRequest._authRetry = true;

        try {
          const tokenResult = await acquireBackendToken(instance, accounts[0], {
            forceRefresh: true,
          });

          if (!tokenResult?.accessToken) {
            return Promise.reject(error);
          }

          originalRequest.headers = withAuthHeader(
            tokenResult.accessToken,
            originalRequest.headers || {}
          );

          return axios(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [instance, accounts]);

  return children;
}

export default ApiAuthProvider;
